import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { request } from "../../services/api";
import { CircuitBackgroundLayer } from "../../components/CircuitBackground/AnimatedCircuitChipBackground";
import sidebarLogo from "../../images/sidebar-logo-160.png";
import UserMenu from "../../components/UserMenu/UserMenu";

import { LuHardDriveDownload, LuHardDriveUpload } from "react-icons/lu";

import {
  FaThermometerHalf,
  FaTint,
  FaWind,
  FaArrowLeft,
  FaRss,
  FaPhoneAlt,
  FaRedo,
  FaShieldAlt,
  FaCogs,
} from "react-icons/fa";
import styles from "./Monitor.module.css";
import { useMonitorSocket } from "../../hooks/useMonitorSocket";
import SensorGrid from "./SensorGrid";
import ExpandableTile from "../../components/common/ExpandableTile";
import DigitalInputCard from "./DigitalInputCard";
import DigitalOutputCard from "./DigitalOutputCard";
import SensorControlCard from "./SensorControlCard";

import DigitalInputMonitorCard from "./DigitalInputMonitorCard";
import DigitalOutputMonitorCard from "./DigitalOutputMonitorCard";
import SensorMonitorCard from "./SensorMonitorCard";

import SecurityCard from "./SecurityCard";
import GsmControlCard from "./GsmControlCard";
import RemoteControlCard from "./RemoteControlCard";
import { useLocalTheme } from "../../theme/useLocalTheme";
import { readOfflineCache, writeOfflineCache } from "../../utils/offlineCache";

const DEFAULT_MONITOR_TILE_ORDER = ["temp", "humi", "dIn", "dOut", "wind"];

const MOBILE_SETTINGS_SIDEBAR_QUERY = "(max-width: 700px)";

const isMobileSettingsSidebarViewport = () => {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return false;
  }

  return window.matchMedia(MOBILE_SETTINGS_SIDEBAR_QUERY).matches;
};

const getInitialSettingsSidebarOpen = () => {
  if (isMobileSettingsSidebarViewport()) {
    return false;
  }

  if (typeof localStorage === "undefined") {
    return true;
  }

  return localStorage.getItem("sfarm_settings_sidebar_open") !== "0";
};

const getMonitorOrderStorageKey = (deviceId) => {
  return `sfarm_monitor_tile_order_${deviceId || "default"}`;
};

const createEmptyMonitorMap = () => ({
  dIn: {},
  dOut: {},
  temp: {},
  humi: {},
  wind: {},
});

const isActiveConnectionValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE" ||
    value === "on" ||
    value === "ON" ||
    value === "connected"
  );
};

const hasReadableValue = (value) => {
  return (
    value !== undefined &&
    value !== null &&
    value !== "" &&
    value !== "--" &&
    value !== "---" &&
    value !== "N/A" &&
    value !== "nan" &&
    value !== "NaN"
  );
};

const isSensorVisibleForLayout = (data) => {
  if (!data) return false;

  const connected =
    data.connected ?? data.isConnect ?? data.isconnect ?? data.is_connected;

  if (connected !== undefined && connected !== null) {
    return isActiveConnectionValue(connected);
  }

  return (
    hasReadableValue(data.current) ||
    hasReadableValue(data.value) ||
    hasReadableValue(data.status)
  );
};

const isVisibleByUser = (visibilityMap, index) => {
  if (!visibilityMap) return true;

  const value = visibilityMap[index] ?? visibilityMap[String(index)];

  if (value === undefined || value === null) {
    return true;
  }

  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE"
  );
};
const settingsPanelColorClassMap = {
  dIn: styles.settingsPanelDIn,
  dOut: styles.settingsPanelDOut,
  temp: styles.settingsPanelTemp,
  humi: styles.settingsPanelHumi,
  wind: styles.settingsPanelWind,
  security: styles.settingsPanelSecurity,
  gsm: styles.settingsPanelGsm,
  remote: styles.settingsPanelRemote,
};

const getUserVisibleCount = (count, visibilityMap = {}) => {
  let userVisibleCount = 0;

  for (let i = 0; i < count; i++) {
    if (isVisibleByUser(visibilityMap, i)) {
      userVisibleCount++;
    }
  }

  return userVisibleCount;
};

const getVisibleSensorCount = (
  sensorData,
  suspendedSensors,
  type,
  count,
  visibilityMap = {},
) => {
  let visibleCount = 0;

  for (let i = 0; i < count; i++) {
    if (!isVisibleByUser(visibilityMap, i)) {
      continue;
    }

    const key = `${type}${i}`;

    if (suspendedSensors?.has?.(key)) {
      continue;
    }

    const data = sensorData?.[key];

    if (isSensorVisibleForLayout(data)) {
      visibleCount++;
    }
  }

  return visibleCount;
};

const getFilteredSensorDataByVisibility = (
  sensorData,
  type,
  count,
  visibilityMap = {},
) => {
  if (!sensorData) return {};

  const filteredData = { ...sensorData };

  for (let i = 0; i < count; i++) {
    if (!isVisibleByUser(visibilityMap, i)) {
      delete filteredData[`${type}${i}`];
    }
  }

  return filteredData;
};

const getSmartTileSpan = (visibleCount) => {
  if (visibleCount <= 2) return 1;
  if (visibleCount <= 4) return 2;
  return 4;
};
const getSmartTileWidth = (visibleCount) => {
  const count = Math.max(1, visibleCount);

  const cardWidth = 145;
  const cardGap = 10;
  const tilePadding = 38;
  const minTileWidth = 220;
  const maxTileWidth = 1280;

  const calculatedWidth =
    count * cardWidth + Math.max(0, count - 1) * cardGap + tilePadding;

  return Math.min(maxTileWidth, Math.max(minTileWidth, calculatedWidth));
};

const normalizeLastStateType = (type) => {
  const normalized = String(type || "")
    .trim()
    .toLowerCase();

  if (normalized === "din" || normalized === "d_in" || normalized === "d-in") {
    return "dIn";
  }

  if (
    normalized === "dout" ||
    normalized === "d_out" ||
    normalized === "d-out"
  ) {
    return "dOut";
  }

  if (normalized === "humidity" || normalized === "humid") return "humi";

  return normalized;
};

const getFirstReadableValue = (...values) => {
  for (const value of values) {
    if (hasReadableValue(value)) return value;
  }

  return "--";
};

const getObjectValue = (source, ...keys) => {
  if (!source || typeof source !== "object") return undefined;

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const value = source[key];

      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
  }

  return undefined;
};

const normalizeLastProps = (row = {}) => {
  const props = row.lastProps ?? row.last_props ?? row.last_props_json;

  if (!props) return {};

  if (typeof props === "object") return props;

  try {
    const parsed = JSON.parse(String(props));
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const getLastStateProp = (row, props, ...names) => {
  for (const name of names) {
    const fromProps = getObjectValue(props, name);
    if (fromProps !== undefined) return fromProps;

    const fromRow = getObjectValue(
      row,
      name,
      `last${name[0]?.toUpperCase?.() || ""}${name.slice(1)}`,
    );
    if (fromRow !== undefined) return fromRow;
  }

  return "";
};

const isTruthyStateValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE" ||
    value === "on" ||
    value === "ON"
  );
};

const parseMonitorDateValue = (value) => {
  if (!value) return { date: null, raw: "" };

  const raw = String(value).trim();
  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?/,
  );

  const date = match
    ? new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
      )
    : new Date(raw.replace(" ", "T"));

  if (Number.isNaN(date.getTime())) {
    return { date: null, raw };
  }

  return { date, raw };
};

const formatMonitorLastSeenAt = (value) => {
  const { date, raw } = parseMonitorDateValue(value);

  if (!date) return raw || "نامشخص";

  try {
    return date.toLocaleString("fa-IR-u-ca-persian", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return raw || "نامشخص";
  }
};

const formatMonitorLastChangeAt = (value) => {
  const { date, raw } = parseMonitorDateValue(value);

  if (!date) return raw || "نامشخص";

  try {
    const parts = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);

    const getPart = (type) => {
      const part = parts.find((item) => item.type === type);
      return part?.value || "";
    };

    const year = getPart("year");
    const shortYear = year.length > 2 ? year.slice(-2) : year;
    const month = getPart("month");
    const day = getPart("day");
    const hour = getPart("hour");
    const minute = getPart("minute");

    if (!shortYear || !month || !day || !hour || !minute) {
      return formatMonitorLastSeenAt(value);
    }

    return `${hour}:${minute} ٬  ${shortYear}/${month}/${day}`;
  } catch {
    return raw || "نامشخص";
  }
};

const getLastStateKey = (type, index) => {
  if (type === "wind") return "wind0";

  return `${type}${Number.isInteger(index) ? index : 0}`;
};

const buildLastStateSensorData = (rows, deviceMqttLastSeenAt, options = {}) => {
  const preview = {};

  if (!Array.isArray(rows)) return preview;

  const deviceMqttLastSeenLabel = formatMonitorLastSeenAt(deviceMqttLastSeenAt);
  const includeHidden = Boolean(options?.includeHidden);

  rows.forEach((row) => {
    if (!row) return;

    const rowVisible = row.isVisible ?? row.is_visible;

    if (
      !includeHidden &&
      (rowVisible === false || rowVisible === 0 || rowVisible === "0")
    ) {
      return;
    }

    const type = normalizeLastStateType(
      row.type || row.sensorType || row.sensor_type,
    );

    if (!["temp", "humi", "wind", "dIn", "dOut"].includes(type)) {
      return;
    }

    const index = Number(row.index ?? row.sensor_index ?? 0);

    if (!Number.isInteger(index) || index < 0) return;

    const props = normalizeLastProps(row);
    const lastSeenAt = row.lastSeenAt || row.last_seen_at || row.lastSeen || "";
    const lastSeenLabel = formatMonitorLastChangeAt(lastSeenAt);
    const value = getFirstReadableValue(
      row.lastNumericValue,
      row.last_numeric_value,
      row.value,
      row.valueText,
      row.lastValueText,
      row.last_value_text,
      getLastStateProp(row, props, "value", "current", "status"),
    );

    // A database row without an actual recorded value is not a real monitor
    // item and must not create an offline placeholder card.
    if (!hasReadableValue(value)) return;

    const setpoint = getFirstReadableValue(
      row.setpoint,
      row.lastSetpoint,
      row.last_setpoint,
      getLastStateProp(row, props, "setpoint", "setPoint", "set_point"),
    );
    const trigValue =
      row.trig ??
      row.lastTrig ??
      row.last_trig ??
      getLastStateProp(row, props, "trig", "triggered");
    const enableValue =
      row.enable ??
      row.lastEnable ??
      row.last_enable ??
      getLastStateProp(row, props, "enable");
    const isConnectValue =
      row.isConnect ??
      row.lastIsConnect ??
      row.last_is_connect ??
      getLastStateProp(row, props, "isConnect", "is_connect", "connected");
    const sensorKey = getLastStateKey(type, index);

    const lastSettings = {
      setpoint: hasReadableValue(setpoint) ? setpoint : "",
      hyster: getLastStateProp(row, props, "hyster", "hysteresis"),
      leadDelay: getLastStateProp(row, props, "leadDelay", "lead_delay"),
      lagDelay: getLastStateProp(row, props, "lagDelay", "lag_delay"),
      reverse: getLastStateProp(row, props, "reverse"),
      alertSms: getLastStateProp(row, props, "alertSms", "alert_sms"),
      toDOut: getLastStateProp(row, props, "toDOut", "to_dout", "toDout"),
      enable: enableValue ?? "",
      logicMode: getLastStateProp(row, props, "logicMode", "logic_mode"),
      timerMode: getLastStateProp(row, props, "timerMode", "timer_mode"),
      delayOn: getLastStateProp(row, props, "delayOn", "delay_on"),
      delayOff: getLastStateProp(row, props, "delayOff", "delay_off"),
      periodicOn: getLastStateProp(row, props, "periodicOn", "periodic_on"),
      periodicOff: getLastStateProp(row, props, "periodicOff", "periodic_off"),
      dailyOn: getLastStateProp(row, props, "dailyOn", "daily_on"),
      dailyOff: getLastStateProp(row, props, "dailyOff", "daily_off"),
    };

    preview[sensorKey] = {
      connected: true,
      isOfflinePreview: true,
      isLastStatePreview: true,
      current: value,
      value,
      status: value,
      triggered: isTruthyStateValue(trigValue),
      isConnect: isConnectValue,
      lastSeenAt,
      lastSeenLabel,
      deviceMqttLastSeenAt,
      deviceMqttLastSeenLabel,
      settings: lastSettings,
    };
  });

  return preview;
};

const buildOfflinePreviewData = (...sources) => {
  const mergedData = {};

  sources.filter(Boolean).forEach((source) => {
    Object.entries(source).forEach(([key, value]) => {
      const hasRecordedValue =
        value?.hasEverReported === true ||
        hasReadableValue(value?.current) ||
        hasReadableValue(value?.value) ||
        hasReadableValue(value?.status);

      if (!hasRecordedValue) return;

      mergedData[key] = {
        ...(mergedData[key] || {}),
        ...(value || {}),
      };
    });
  });

  return Object.fromEntries(
    Object.entries(mergedData).map(([key, value]) => [
      key,
      {
        ...(value || {}),
        isOfflinePreview: true,
        readOnlyPreview: true,
      },
    ]),
  );
};

const normalizePermissionValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE"
  );
};

const hasPermissionPayload = (permissions) => {
  return (
    permissions &&
    typeof permissions === "object" &&
    Object.keys(permissions).length > 0
  );
};

const readJsonFromStorage = (storage, key) => {
  try {
    const rawValue = storage.getItem(key);

    if (!rawValue) return null;

    const parsedValue = JSON.parse(rawValue);

    return hasPermissionPayload(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
};

const MonitorPage = () => {
  useLocalTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const getInitialDevicePermissions = () => {
    const statePermissions =
      location.state?.permissions || location.state?.device?.permissions;

    if (hasPermissionPayload(statePermissions)) {
      sessionStorage.setItem(
        `device_permissions_${id}`,
        JSON.stringify(statePermissions),
      );
      writeOfflineCache("device-permissions", id, statePermissions);

      return statePermissions;
    }

    const sessionPermissions = readJsonFromStorage(
      sessionStorage,
      `device_permissions_${id}`,
    );

    if (sessionPermissions) return sessionPermissions;

    const cachedPermissions = readOfflineCache("device-permissions", id);
    return hasPermissionPayload(cachedPermissions?.data)
      ? cachedPermissions.data
      : null;
  };

  const [initialDevicePermissions] = useState(getInitialDevicePermissions);
  const [devicePermissions, setDevicePermissions] = useState(
    initialDevicePermissions,
  );

  const [isPermissionLoading, setIsPermissionLoading] = useState(
    () => !initialDevicePermissions,
  );
  const [isSettingsSidebarOpen, setIsSettingsSidebarOpen] = useState(
    getInitialSettingsSidebarOpen,
  );
  const [isBrowserOnline, setIsBrowserOnline] = useState(() => {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine !== false;
  });

  const toggleSettingsSidebar = () => {
    setIsSettingsSidebarOpen((prev) => {
      const nextValue = !prev;
      localStorage.setItem(
        "sfarm_settings_sidebar_open",
        nextValue ? "1" : "0",
      );
      return nextValue;
    });
  };

  useEffect(() => {
    const handleBrowserOnline = () => setIsBrowserOnline(true);
    const handleBrowserOffline = () => setIsBrowserOnline(false);

    window.addEventListener("online", handleBrowserOnline);
    window.addEventListener("offline", handleBrowserOffline);

    setIsBrowserOnline(navigator.onLine !== false);

    return () => {
      window.removeEventListener("online", handleBrowserOnline);
      window.removeEventListener("offline", handleBrowserOffline);
    };
  }, []);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_SETTINGS_SIDEBAR_QUERY);

    const syncMobileSidebarState = () => {
      if (mediaQuery.matches) {
        setIsSettingsSidebarOpen(false);
      }
    };

    syncMobileSidebarState();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncMobileSidebarState);

      return () => {
        mediaQuery.removeEventListener("change", syncMobileSidebarState);
      };
    }

    mediaQuery.addListener(syncMobileSidebarState);

    return () => {
      mediaQuery.removeListener(syncMobileSidebarState);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadDevicePermissions = async () => {
      const cachedPermissions = readJsonFromStorage(
        sessionStorage,
        `device_permissions_${id}`,
      ) || readOfflineCache("device-permissions", id)?.data;

      if (cachedPermissions) {
        setDevicePermissions(cachedPermissions);
        setIsPermissionLoading(false);
        return;
      }

      setIsPermissionLoading(true);

      try {
        const data = await request("/get_devices.php");

        if (cancelled) return;

        if (data?.status !== "success" || !Array.isArray(data.devices)) {
          setDevicePermissions(null);
          return;
        }

        const currentDevice = data.devices.find(
          (item) => String(item.id) === String(id),
        );

        if (!currentDevice?.permissions) {
          setDevicePermissions(null);
          return;
        }

        sessionStorage.setItem(
          `device_permissions_${id}`,
          JSON.stringify(currentDevice.permissions),
        );

        sessionStorage.setItem(`device_${id}`, JSON.stringify(currentDevice));

        setDevicePermissions(currentDevice.permissions);
        writeOfflineCache(
          "device-permissions",
          id,
          currentDevice.permissions,
        );
        writeOfflineCache("device", id, currentDevice);
      } catch (error) {
        console.error("Error loading device permissions:", error);

        if (!cancelled) {
          setDevicePermissions(null);
        }
      } finally {
        if (!cancelled) {
          setIsPermissionLoading(false);
        }
      }
    };

    if (id) {
      loadDevicePermissions();
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  const [customNames, setCustomNames] = useState(() => {
    const cachedNames = readOfflineCache("monitor-custom-names", id);
    return cachedNames?.data || createEmptyMonitorMap();
  });

  const [visibilitySettings, setVisibilitySettings] = useState(() => {
    const cachedVisibility = readOfflineCache("monitor-visibility", id);
    return cachedVisibility?.data || createEmptyMonitorMap();
  });

  const [lastStateRows, setLastStateRows] = useState(() => {
    const cachedState = readOfflineCache("device-last-state", id);
    return Array.isArray(cachedState?.data?.sensors)
      ? cachedState.data.sensors
      : [];
  });
  const [deviceMqttLastSeenAt, setDeviceMqttLastSeenAt] = useState(() => {
    const cachedState = readOfflineCache("device-last-state", id);
    return cachedState?.data?.deviceMqttLastSeenAt || "";
  });

  const [monitorTileOrder, setMonitorTileOrder] = useState(
    DEFAULT_MONITOR_TILE_ORDER,
  );
  const [activeSettingsPanel, setActiveSettingsPanel] = useState(null);
  const [activeSettingsTargetIndex, setActiveSettingsTargetIndex] =
    useState(null);

  const {
    device,
    sensorData,
    wsConnected,
    liveDataReady,
    suspendedSensors,
    sendCommand,
    handleRetryAll,
  } = useMonitorSocket(id);

  const canMonitor = normalizePermissionValue(devicePermissions?.can_monitor);
  const canConfigure = normalizePermissionValue(
    devicePermissions?.can_configure,
  );
  const canControlOutputs = normalizePermissionValue(
    devicePermissions?.can_control_outputs,
  );
  const canManageUsers = normalizePermissionValue(
    devicePermissions?.can_manage_users,
  );
  const isDeviceOwner = normalizePermissionValue(devicePermissions?.is_owner);

  useEffect(() => {
    if (id && hasPermissionPayload(devicePermissions)) {
      writeOfflineCache("device-permissions", id, devicePermissions);
    }
  }, [devicePermissions, id]);

  useEffect(() => {
    if (id) writeOfflineCache("monitor-custom-names", id, customNames);
  }, [customNames, id]);

  useEffect(() => {
    if (id) writeOfflineCache("monitor-visibility", id, visibilitySettings);
  }, [id, visibilitySettings]);

  useEffect(() => {
    if (id && lastStateRows.length > 0) {
      writeOfflineCache("device-last-state", id, {
        sensors: lastStateRows,
        deviceMqttLastSeenAt,
      });
    }
  }, [deviceMqttLastSeenAt, id, lastStateRows]);

  useEffect(() => {
    let cancelled = false;

    const loadAllCustomNames = async () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;

      try {
        const [dIn, dOut, temp, humi, wind] = await Promise.all([
          request(`/manage_input_names.php?device_id=${id}&input_type=dIn`),
          request(`/manage_input_names.php?device_id=${id}&input_type=dOut`),
          request(`/manage_input_names.php?device_id=${id}&input_type=temp`),
          request(`/manage_input_names.php?device_id=${id}&input_type=humi`),
          request(`/manage_input_names.php?device_id=${id}&input_type=wind`),
        ]);

        if (cancelled) return;

        setCustomNames({
          dIn: dIn || {},
          dOut: dOut || {},
          temp: temp || {},
          humi: humi || {},
          wind: wind || {},
        });
      } catch (error) {
        console.error("Error loading custom names:", error);
      }
    };

    if (id) {
      loadAllCustomNames();
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const loadAllVisibilitySettings = async () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;

      try {
        const [dIn, dOut, temp, humi, wind] = await Promise.all([
          request(
            `/manage_input_visibility.php?device_id=${id}&input_type=dIn`,
          ),
          request(
            `/manage_input_visibility.php?device_id=${id}&input_type=dOut`,
          ),
          request(
            `/manage_input_visibility.php?device_id=${id}&input_type=temp`,
          ),
          request(
            `/manage_input_visibility.php?device_id=${id}&input_type=humi`,
          ),
          request(
            `/manage_input_visibility.php?device_id=${id}&input_type=wind`,
          ),
        ]);

        if (cancelled) return;

        setVisibilitySettings({
          dIn: dIn || {},
          dOut: dOut || {},
          temp: temp || {},
          humi: humi || {},
          wind: wind || {},
        });
      } catch (error) {
        console.error("Error loading visibility settings:", error);
      }
    };

    if (id) {
      loadAllVisibilitySettings();
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    let refreshTimer = null;

    const loadLastStatePreview = async () => {
      if (!id) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;

      try {
        const data = await request(
          `/get_device_sensor_last_state.php?device_id=${id}${
            canConfigure ? "&include_hidden=1" : ""
          }`,
        );

        if (cancelled) return;

        if (Array.isArray(data)) {
          setLastStateRows(data);
          return;
        }

        if (data?.status === "success" && Array.isArray(data.sensors)) {
          setLastStateRows(data.sensors);
          const nextLastSeenAt =
            data.deviceMqttLastSeenAt ||
            data.mqttLastSeenAt ||
            data.device_mqtt_last_seen_at ||
            "";

          setDeviceMqttLastSeenAt(nextLastSeenAt);
          return;
        }

        console.warn("Monitor last-state response was not usable:", data);
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading monitor last-state preview:", error);
          // Keep previous cached last-state data, so the offline preview does not blink away during retry.
        }
      }
    };

    loadLastStatePreview();

    if (!wsConnected || !liveDataReady) {
      refreshTimer = window.setInterval(loadLastStatePreview, 30000);
    }

    return () => {
      cancelled = true;

      if (refreshTimer) {
        window.clearInterval(refreshTimer);
      }
    };
  }, [id, wsConnected, liveDataReady, canConfigure]);

  useEffect(() => {
    const storageKey = getMonitorOrderStorageKey(id);

    try {
      const savedOrder = localStorage.getItem(storageKey);

      if (!savedOrder) {
        setMonitorTileOrder(DEFAULT_MONITOR_TILE_ORDER);
        return;
      }

      const parsedOrder = JSON.parse(savedOrder);

      if (!Array.isArray(parsedOrder)) {
        setMonitorTileOrder(DEFAULT_MONITOR_TILE_ORDER);
        return;
      }

      const validOrder = parsedOrder.filter((item) =>
        DEFAULT_MONITOR_TILE_ORDER.includes(item),
      );

      const missingItems = DEFAULT_MONITOR_TILE_ORDER.filter(
        (item) => !validOrder.includes(item),
      );

      setMonitorTileOrder([...validOrder, ...missingItems]);
    } catch (error) {
      console.error("Error loading monitor tile order:", error);
      setMonitorTileOrder(DEFAULT_MONITOR_TILE_ORDER);
    }
  }, [id]);

  const handleSaveCustomName = async (type, index, newName) => {
    if (!canConfigure) {
      alert("شما اجازه تغییر تنظیمات این دستگاه را ندارید.");
      return;
    }

    try {
      await request("/manage_input_names.php", "POST", {
        device_id: id,
        input_type: type,
        input_index: index,
        customName: newName,
      });

      setCustomNames((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          [index]: newName,
        },
      }));
    } catch (error) {
      console.error("Error saving custom name:", error);
    }
  };

  const handleSaveVisibility = async (type, index, isVisible) => {
    if (!canConfigure) {
      alert("شما اجازه تغییر تنظیمات نمایش این دستگاه را ندارید.");
      return;
    }

    const inputIndex = Number(index);

    console.log("SAVE VISIBILITY:", {
      type,
      index,
      inputIndex,
      isVisible,
    });

    if (!type || !Number.isInteger(inputIndex)) {
      console.error("شماره آیتم نامعتبر است:", {
        type,
        index,
        isVisible,
      });

      alert("شماره سنسور / ورودی نامعتبر است");
      return;
    }

    try {
      await request("/manage_input_visibility.php", "POST", {
        device_id: id,
        input_type: type,
        input_index: inputIndex,
        is_visible: isVisible ? 1 : 0,
      });

      setVisibilitySettings((prev) => ({
        ...prev,
        [type]: {
          ...(prev[type] || {}),
          [inputIndex]: isVisible,
        },
      }));
    } catch (error) {
      console.error("خطا در ذخیره تنظیمات نمایش:", error);
      alert("خطا در ذخیره تنظیمات نمایش");
      throw error;
    }
  };

  const openSettingsPanel = (panelName, targetIndex = null) => {
    if (!canConfigure) return;

    const numericTargetIndex =
      targetIndex !== null && Number.isInteger(Number(targetIndex))
        ? Number(targetIndex)
        : null;

    setActiveSettingsTargetIndex(numericTargetIndex);
    setActiveSettingsPanel(panelName);
  };

  const closeSettingsPanel = () => {
    setActiveSettingsPanel(null);
    setActiveSettingsTargetIndex(null);
  };

  useEffect(() => {
    if (!canConfigure && activeSettingsPanel) {
      setActiveSettingsPanel(null);
      setActiveSettingsTargetIndex(null);
    }
  }, [canConfigure, activeSettingsPanel]);

  if (!device || isPermissionLoading) {
    return (
      <div className={styles.container}>
        <CircuitBackgroundLayer />
        <div className={styles.overlay}></div>

        <div
          className={styles.content}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          در حال بارگذاری...
        </div>
      </div>
    );
  }

  if (!canMonitor) {
    return (
      <div className={styles.container}>
        <CircuitBackgroundLayer />
        <div className={styles.overlay}></div>

        <div
          className={styles.content}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "14px",
            color: "#fff",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "1.1rem", fontWeight: "bold" }}>
            شما اجازه مشاهده این دستگاه را ندارید.
          </div>

          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className={`${styles.headerRoundBtn} ${styles.backBtn}`}
            aria-label="بازگشت به داشبورد"
          >
            <FaArrowLeft />
          </button>
        </div>
      </div>
    );
  }

  const safeSensorData = sensorData || {};
  const safeSuspendedSensors = suspendedSensors || new Set();
  const monitorLastStateSensorData = buildLastStateSensorData(
    lastStateRows,
    deviceMqttLastSeenAt,
    { includeHidden: false },
  );
  const settingsLastStateSensorData = buildLastStateSensorData(
    lastStateRows,
    deviceMqttLastSeenAt,
    { includeHidden: true },
  );
  const monitorSections = [
    {
      id: "dIn",
      title: "ورودی‌های دیجیتال",
      icon: <LuHardDriveDownload color="#f6ad55" />,
      type: "dIn",
      count: 68,
      label: "ورودی",
      unit: "",
      CardComponent: DigitalInputMonitorCard,
      customNames: customNames.dIn,
      visibilitySettings: visibilitySettings.dIn,
    },
    {
      id: "dOut",
      title: "کنترل خروجی‌ها",
      icon: <LuHardDriveUpload color="#39ff14" />,
      type: "dOut",
      count: 72,
      label: "خروجی",
      unit: "",
      CardComponent: DigitalOutputMonitorCard,
      customNames: customNames.dOut,
      visibilitySettings: visibilitySettings.dOut,
    },
    {
      id: "temp",
      title: "کنترل دما",
      icon: <FaThermometerHalf color="#ff6b6b" />,
      type: "temp",
      count: 9,
      label: "دما",
      unit: "°C",
      CardComponent: SensorMonitorCard,
      customNames: customNames.temp,
      visibilitySettings: visibilitySettings.temp,
    },
    {
      id: "humi",
      title: "کنترل رطوبت",
      icon: <FaTint color="#4dabf7" />,
      type: "humi",
      count: 9,
      label: "رطوبت",
      unit: "%",
      CardComponent: SensorMonitorCard,
      customNames: customNames.humi,
      visibilitySettings: visibilitySettings.humi,
    },
    {
      id: "wind",
      title: "سنجش باد",
      icon: <FaWind color="#cbd5e0" />,
      type: "wind",
      count: 1,
      label: "باد",
      unit: "km/h",
      CardComponent: SensorMonitorCard,
      customNames: customNames.wind,
      visibilitySettings: visibilitySettings.wind,
    },
  ];

  // Server last-state fills gaps; the locally cached live values take priority
  // because they are the last values the user actually saw on this device.
  const monitorOfflineSensorData = buildOfflinePreviewData(
    monitorLastStateSensorData,
    safeSensorData,
  );
  const settingsOfflineSensorData = buildOfflinePreviewData(
    settingsLastStateSensorData,
    safeSensorData,
  );
  const hasOfflinePreview = Object.keys(monitorOfflineSensorData).length > 0;
  const hasSettingsOfflinePreview =
    Object.keys(settingsOfflineSensorData).length > 0;
  const isLiveAvailable = isBrowserOnline && wsConnected && liveDataReady;
  const isLiveUnavailable = !isLiveAvailable;
  const shouldUseLastStatePreview =
    isLiveUnavailable && hasOfflinePreview;

  const displaySensorData = shouldUseLastStatePreview
    ? monitorOfflineSensorData
    : safeSensorData;

  const displaySuspendedSensors = shouldUseLastStatePreview
    ? new Set()
    : safeSuspendedSensors;

  const deviceMqttLastSeenLabel = formatMonitorLastSeenAt(deviceMqttLastSeenAt);

  const activeSettingsSection = monitorSections.find(
    (section) => section.id === activeSettingsPanel,
  );
  const shouldUseSettingsLastStatePreview = Boolean(
    activeSettingsSection &&
    hasSettingsOfflinePreview &&
    isLiveUnavailable,
  );

  const settingsSensorData = shouldUseSettingsLastStatePreview
    ? settingsOfflineSensorData
    : safeSensorData;

  const settingsSuspendedSensors = shouldUseSettingsLastStatePreview
    ? new Set()
    : safeSuspendedSensors;

  const settingsUpdateHandler = shouldUseSettingsLastStatePreview
    ? undefined
    : sendCommand;

  const renderSettingsOfflineNotice = () => {
    if (!shouldUseSettingsLastStatePreview) return null;

    return (
      <div className={styles.settingsOfflinePreviewNotice}>
        <strong>نمایش آخرین کانفیگ ذخیره‌شده</strong>
        <span>
          دستگاه آفلاین است؛ تغییر تنظیمات و ارسال فرمان غیرفعال شده است.
        </span>
      </div>
    );
  };

  const orderedMonitorSections = monitorTileOrder
    .map((sectionId) =>
      monitorSections.find((section) => section.id === sectionId),
    )
    .filter(Boolean)
    .map((section) => {
      const visibleCount = getVisibleSensorCount(
        displaySensorData,
        displaySuspendedSensors,
        section.type,
        section.count,
        section.visibilitySettings,
      );

      const userVisibleCount = getUserVisibleCount(
        section.count,
        section.visibilitySettings,
      );

      const tileVisibleCount = Math.max(1, visibleCount);

      return {
        ...section,
        visibleCount,
        userVisibleCount,
        tileVisibleCount,
        tileWidth: getSmartTileWidth(tileVisibleCount),
      };
    })
    .filter((section) => section.visibleCount > 0);

  const renderSettingsPanelTitle = () => {
    switch (activeSettingsPanel) {
      case "dIn":
        return "تنظیمات ورودی‌های دیجیتال";
      case "dOut":
        return "تنظیمات خروجی‌ها";
      case "temp":
        return "تنظیمات دما";
      case "humi":
        return "تنظیمات رطوبت";
      case "wind":
        return "تنظیمات باد";
      case "security":
        return "امنیت و حفاظت";
      case "gsm":
        return "تنظیمات GSM";
      case "remote":
        return "ریموت کنترلر";
      default:
        return "تنظیمات";
    }
  };

  const renderSettingsPanelContent = () => {
    if (!canConfigure) {
      return (
        <div className={styles.emptySection}>
          شما اجازه تغییر تنظیمات این دستگاه را ندارید.
        </div>
      );
    }

    switch (activeSettingsPanel) {
      case "dIn":
        return (
          <>
            {renderSettingsOfflineNotice()}
            <SensorGrid
              type="dIn"
              count={68}
              onlyIndex={activeSettingsTargetIndex}
              label="ورودی"
              icon={<LuHardDriveDownload color="#f6ad55" />}
              unit=""
              sensorData={settingsSensorData}
              suspendedSensors={settingsSuspendedSensors}
              onUpdate={settingsUpdateHandler}
              CardComponent={DigitalInputCard}
              customNames={customNames.dIn}
              onSaveCustomName={
                shouldUseSettingsLastStatePreview
                  ? undefined
                  : (index, name) => handleSaveCustomName("dIn", index, name)
              }
              visibilitySettings={visibilitySettings.dIn}
              showVisibilityToggle={true}
              filterByVisibility={false}
              onSaveVisibility={
                shouldUseSettingsLastStatePreview
                  ? undefined
                  : handleSaveVisibility
              }
              readOnlyPreview={shouldUseSettingsLastStatePreview}
              canConfigure={canConfigure && !shouldUseSettingsLastStatePreview}
              canControlOutputs={
                canControlOutputs && !shouldUseSettingsLastStatePreview
              }
            />
          </>
        );

      case "dOut":
        return (
          <>
            {renderSettingsOfflineNotice()}
            <SensorGrid
              type="dOut"
              count={72}
              onlyIndex={activeSettingsTargetIndex}
              label="خروجی"
              icon={<LuHardDriveUpload color="#39ff14" />}
              unit=""
              sensorData={settingsSensorData}
              suspendedSensors={settingsSuspendedSensors}
              onUpdate={settingsUpdateHandler}
              CardComponent={DigitalOutputCard}
              customNames={customNames.dOut}
              allCustomNames={customNames}
              onSaveCustomName={
                shouldUseSettingsLastStatePreview
                  ? undefined
                  : (index, name) => handleSaveCustomName("dOut", index, name)
              }
              visibilitySettings={visibilitySettings.dOut}
              showVisibilityToggle={true}
              filterByVisibility={false}
              onSaveVisibility={
                shouldUseSettingsLastStatePreview
                  ? undefined
                  : handleSaveVisibility
              }
              readOnlyPreview={shouldUseSettingsLastStatePreview}
              canConfigure={canConfigure && !shouldUseSettingsLastStatePreview}
              canControlOutputs={
                canControlOutputs && !shouldUseSettingsLastStatePreview
              }
            />
          </>
        );

      case "temp":
        return (
          <>
            {renderSettingsOfflineNotice()}
            <SensorGrid
              type="temp"
              count={9}
              onlyIndex={activeSettingsTargetIndex}
              label="دما"
              icon={<FaThermometerHalf color="#ff6b6b" />}
              unit="°C"
              sensorData={settingsSensorData}
              suspendedSensors={settingsSuspendedSensors}
              onUpdate={settingsUpdateHandler}
              CardComponent={SensorControlCard}
              customNames={customNames.temp}
              onSaveCustomName={
                shouldUseSettingsLastStatePreview
                  ? undefined
                  : (index, name) => handleSaveCustomName("temp", index, name)
              }
              serial={device.device_serial}
              token={token}
              visibilitySettings={visibilitySettings.temp}
              showVisibilityToggle={true}
              onSaveVisibility={
                shouldUseSettingsLastStatePreview
                  ? undefined
                  : handleSaveVisibility
              }
              readOnlyPreview={shouldUseSettingsLastStatePreview}
              canConfigure={canConfigure && !shouldUseSettingsLastStatePreview}
              canControlOutputs={
                canControlOutputs && !shouldUseSettingsLastStatePreview
              }
            />
          </>
        );

      case "humi":
        return (
          <>
            {renderSettingsOfflineNotice()}
            <SensorGrid
              type="humi"
              count={9}
              onlyIndex={activeSettingsTargetIndex}
              label="رطوبت"
              icon={<FaTint color="#4dabf7" />}
              unit="%"
              sensorData={settingsSensorData}
              suspendedSensors={settingsSuspendedSensors}
              onUpdate={settingsUpdateHandler}
              CardComponent={SensorControlCard}
              customNames={customNames.humi}
              onSaveCustomName={
                shouldUseSettingsLastStatePreview
                  ? undefined
                  : (index, name) => handleSaveCustomName("humi", index, name)
              }
              serial={device.device_serial}
              token={token}
              visibilitySettings={visibilitySettings.humi}
              showVisibilityToggle={true}
              onSaveVisibility={
                shouldUseSettingsLastStatePreview
                  ? undefined
                  : handleSaveVisibility
              }
              readOnlyPreview={shouldUseSettingsLastStatePreview}
              canConfigure={canConfigure && !shouldUseSettingsLastStatePreview}
              canControlOutputs={
                canControlOutputs && !shouldUseSettingsLastStatePreview
              }
            />
          </>
        );

      case "wind":
        return (
          <>
            {renderSettingsOfflineNotice()}
            <SensorGrid
              type="wind"
              count={1}
              onlyIndex={activeSettingsTargetIndex}
              label="باد"
              icon={<FaWind color="#cbd5e0" />}
              unit="km/h"
              sensorData={settingsSensorData}
              suspendedSensors={settingsSuspendedSensors}
              onUpdate={settingsUpdateHandler}
              CardComponent={SensorControlCard}
              customNames={customNames.wind}
              onSaveCustomName={
                shouldUseSettingsLastStatePreview
                  ? undefined
                  : (index, name) => handleSaveCustomName("wind", index, name)
              }
              serial={device.device_serial}
              token={token}
              visibilitySettings={visibilitySettings.wind}
              showVisibilityToggle={true}
              onSaveVisibility={
                shouldUseSettingsLastStatePreview
                  ? undefined
                  : (index, isVisible) =>
                      handleSaveVisibility("wind", index, isVisible)
              }
              readOnlyPreview={shouldUseSettingsLastStatePreview}
              canConfigure={canConfigure && !shouldUseSettingsLastStatePreview}
              canControlOutputs={
                canControlOutputs && !shouldUseSettingsLastStatePreview
              }
            />
          </>
        );

      case "security":
        return (
          <div className={styles.sensorsGrid}>
            {safeSensorData["secu0"] ? (
              <SecurityCard
                data={safeSensorData["secu0"]}
                onUpdate={
                  canConfigure
                    ? (prop, val) => sendCommand("secu", 0, prop, val)
                    : undefined
                }
              />
            ) : (
              <div className={styles.emptySection}>
                اطلاعات امنیت و حفاظت هنوز دریافت نشده است.
              </div>
            )}
          </div>
        );

      case "gsm":
        return (
          <div className={styles.sensorsGrid}>
            {safeSensorData["gsm0"] ? (
              <GsmControlCard
                data={safeSensorData["gsm0"]}
                onUpdate={
                  canConfigure
                    ? (prop, val) => sendCommand("gsm", 0, prop, val)
                    : undefined
                }
              />
            ) : (
              <div className={styles.emptySection}>
                اطلاعات GSM هنوز دریافت نشده است.
              </div>
            )}
          </div>
        );

      case "remote":
        return (
          <div className={styles.sensorsGrid}>
            {safeSensorData["remote0"] ? (
              <RemoteControlCard
                data={safeSensorData["remote0"]}
                onUpdate={
                  canConfigure
                    ? (prop, val) => sendCommand("remote", 0, prop, val)
                    : undefined
                }
              />
            ) : (
              <div className={styles.emptySection}>
                اطلاعات ریموت کنترلر هنوز دریافت نشده است.
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <CircuitBackgroundLayer />
      <div className={styles.overlay}></div>

      <div
        className={`
        ${styles.content}
        ${
          canConfigure
            ? isSettingsSidebarOpen
              ? styles.contentWithSidebar
              : styles.contentWithCollapsedSidebar
            : ""
        }
    `}
      >
        <div className={styles.header}>
          <div className={styles.deviceTitle}>
            <span className={styles.deviceTitleIcon}>🏭</span>
            <span className={styles.deviceTitleName}>{device.device_name}</span>
          </div>

          <div className={styles.deviceMetaRow}>
            <span className={styles.deviceSerialCode}>
              {device.device_serial}
            </span>

            <div className={styles.headerActions}>
              {safeSuspendedSensors.size > 0 && (
                <div
                  className={styles.headerActionHint}
                  data-hint={`بازیابی (${safeSuspendedSensors.size})`}
                >
                  <button
                    type="button"
                    onClick={handleRetryAll}
                    className={`${styles.headerRoundBtn} ${styles.retryBtn}`}
                    aria-label={`بازیابی ${safeSuspendedSensors.size} مورد`}
                  >
                    <FaRedo />
                  </button>
                </div>
              )}

              <div className={styles.headerUserMenuSlot}>
                <UserMenu dropdownAlign="left" />
              </div>
              <div className={styles.headerActionHint} data-hint="بازگشت">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className={`${styles.headerRoundBtn} ${styles.backBtn}`}
                  aria-label="بازگشت"
                >
                  <FaArrowLeft />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isLiveUnavailable && (
          <div className={styles.monitorOfflineNotice}>
            <div className={styles.monitorOfflineNoticeTitle}>
              {hasOfflinePreview
                ? "ارتباط قطع است؛ آخرین وضعیت ذخیره‌شده نمایش داده می‌شود و کنترل زنده در دسترس نیست."
                : "ارتباط قطع است؛ هنوز وضعیت ذخیره‌شده‌ای برای نمایش وجود ندارد و کنترل زنده در دسترس نیست."}
            </div>

            {hasReadableValue(deviceMqttLastSeenAt) && (
              <div className={styles.monitorOfflineNoticeMeta}>
                آخرین ارتباط دستگاه با سرور: {deviceMqttLastSeenLabel}
              </div>
            )}
          </div>
        )}

        <div className={styles.monitorTilesGrid}>
          {orderedMonitorSections.map((section) => (
            <ExpandableTile
              key={section.id}
              title={section.title}
              icon={section.icon}
              defaultExpanded={true}
              className={styles.smartMonitorTile}
              style={{
                "--tile-width": `${section.tileWidth}px`,
                "--tile-visible-count": section.tileVisibleCount,
              }}
            >
              <SensorGrid
                type={section.type}
                count={section.count}
                label={section.label}
                icon={section.icon}
                unit={section.unit}
                sensorData={getFilteredSensorDataByVisibility(
                  displaySensorData,
                  section.type,
                  section.count,
                  section.visibilitySettings,
                )}
                suspendedSensors={displaySuspendedSensors}
                onUpdate={shouldUseLastStatePreview ? undefined : sendCommand}
                CardComponent={section.CardComponent}
                customNames={section.customNames}
                allCustomNames={customNames}
                onSaveCustomName={(index, name) =>
                  handleSaveCustomName(section.type, index, name)
                }
                onOpenSettings={(type, index) =>
                  openSettingsPanel(type, index)
                }
                serial={device.device_serial}
                token={token}
                visibilitySettings={section.visibilitySettings}
                filterByVisibility={true}
                canConfigure={canConfigure && !shouldUseLastStatePreview}
                canControlOutputs={
                  canControlOutputs && !shouldUseLastStatePreview
                }
              />
            </ExpandableTile>
          ))}
        </div>
      </div>

      {canConfigure && (
        <aside
          className={`
      ${styles.settingsSidebar}
      ${styles.settingsFocusSurface}
      ${!isSettingsSidebarOpen ? styles.settingsSidebarCollapsed : ""}
    `}
        >
          <div
            className={styles.sidebarStatusBox}
            title={isLiveAvailable ? "دستگاه آنلاین است" : "دستگاه آفلاین است"}
          >
            <span
              className={`
          ${styles.sidebarStatusDot}
          ${isLiveAvailable ? styles.sidebarStatusOnline : styles.sidebarStatusOffline}
      `}
            ></span>

            <span className={styles.sidebarStatusText}>
              {isLiveAvailable ? "آنلاین" : "آفلاین"}
            </span>
          </div>

          <button
            type="button"
            className={styles.sidebarCollapseBtn}
            onClick={toggleSettingsSidebar}
            title={isSettingsSidebarOpen ? "جمع کردن منو" : "باز کردن منو"}
          >
            {isSettingsSidebarOpen ? "›" : "‹"}
          </button>

          <nav className={styles.sidebarNav}>
            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavDIn}`}
              onClick={() => openSettingsPanel("dIn")}
              title="ورودی‌ها"
            >
              <span className={styles.sidebarNavIcon}>
                <LuHardDriveDownload />
              </span>
              <span className={styles.sidebarNavText}>ورودی‌ها</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavDOut}`}
              onClick={() => openSettingsPanel("dOut")}
              title="خروجی‌ها"
            >
              <span className={styles.sidebarNavIcon}>
                <LuHardDriveUpload />
              </span>
              <span className={styles.sidebarNavText}>خروجی‌ها</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavTemp}`}
              onClick={() => openSettingsPanel("temp")}
              title="دما"
            >
              <span className={styles.sidebarNavIcon}>
                <FaThermometerHalf />
              </span>
              <span className={styles.sidebarNavText}>دما</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavHumi}`}
              onClick={() => openSettingsPanel("humi")}
              title="رطوبت"
            >
              <span className={styles.sidebarNavIcon}>
                <FaTint />
              </span>
              <span className={styles.sidebarNavText}>رطوبت</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavWind}`}
              onClick={() => openSettingsPanel("wind")}
              title="باد"
            >
              <span className={styles.sidebarNavIcon}>
                <FaWind />
              </span>
              <span className={styles.sidebarNavText}>باد</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavSecurity}`}
              onClick={() => openSettingsPanel("security")}
              title="امنیت"
            >
              <span className={styles.sidebarNavIcon}>
                <FaShieldAlt />
              </span>
              <span className={styles.sidebarNavText}>امنیت</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavGsm}`}
              onClick={() => openSettingsPanel("gsm")}
              title="GSM"
            >
              <span className={styles.sidebarNavIcon}>
                <FaPhoneAlt />
              </span>
              <span className={styles.sidebarNavText}>GSM</span>
            </button>

            <button
              type="button"
              className={`${styles.sidebarNavItem} ${styles.sidebarNavRemote}`}
              onClick={() => openSettingsPanel("remote")}
              title="ریموت"
            >
              <span className={styles.sidebarNavIcon}>
                <FaRss />
              </span>
              <span className={styles.sidebarNavText}>ریموت</span>
            </button>
          </nav>
          <button
            type="button"
            className={styles.sidebarBrandFooter}
            onClick={() => navigate("/dashboard")}
            title="رفتن به صفحه اصلی"
          >
            <div className={styles.sidebarLogo}>
              <img
                src={sidebarLogo}
                alt="Smart Farm Logo"
                className={styles.sidebarLogoImg}
              />
            </div>

            <div className={styles.sidebarBrandText}>
              <span className={styles.sidebarBrandName}>Smart Farm</span>

              <span className={styles.sidebarBrandSub}>Monitoring</span>
            </div>
          </button>
        </aside>
      )}

      {canConfigure && activeSettingsPanel && (
        <div
          className={styles.settingsDrawerOverlay}
          onClick={closeSettingsPanel}
        >
          <div
            className={`
        ${styles.settingsDrawer}
        ${styles.settingsFocusSurface}
        ${settingsPanelColorClassMap[activeSettingsPanel] || ""}
    `}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.settingsDrawerHeader}>
              <span>
                <FaCogs style={{ marginLeft: "8px" }} />
                {renderSettingsPanelTitle()}
              </span>

              <button
                type="button"
                className={styles.settingsDrawerClose}
                onClick={closeSettingsPanel}
              >
                ×
              </button>
            </div>

            <div className={styles.settingsDrawerBody}>
              {renderSettingsPanelContent()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitorPage;
